import React, { createContext, useContext, useEffect, useMemo } from 'react'
import { useBaseNavItems, resolvePath } from './useNavItems'
import { useLeftNavStore } from './left-nav-store'
import type { NavItemConfig, NavSection } from './types'

interface NavItemsContextValue {
  navItems: NavItemConfig[]
  isLoading: boolean
}

const NavItemsContext = createContext<NavItemsContextValue | null>(null)

export function NavItemsProvider({ children }: { children: React.ReactNode }) {
  const value = useBaseNavItems()
  const syncDefaultPaths = useLeftNavStore((s) => s.syncDefaultPaths)

  const resolvedDefaults = useMemo(
    () =>
      Object.fromEntries(
        value.navItems.map((item) => [item.id, resolvePath(item.path)])
      ) as Partial<Record<NavSection, string>>,
    [value.navItems]
  )

  useEffect(() => {
    syncDefaultPaths(resolvedDefaults)
  }, [resolvedDefaults, syncDefaultPaths])

  return (
    <NavItemsContext.Provider value={value}>
      {children}
    </NavItemsContext.Provider>
  )
}

export function useNavItems(): NavItemsContextValue {
  const ctx = useContext(NavItemsContext)
  if (!ctx) {
    throw new Error('useNavItems must be used within a NavItemsProvider')
  }
  return ctx
}
